import * as qz from 'qz-tray'
import { hextorstr, KEYUTIL, KJUR, stob64 } from 'jsrsasign'
import { QZ_TRAY_CERT, QZ_TRAY_PRIVATE_KEY } from '@/utils/qzTray/qztray'

/**
 * qz-tray 공용 인쇄 클라이언트 — 반품작업_신규 2.0(RGR18)·입고작업 2.0(GL17)이 함께 쓴다.
 *
 * 서명은 아직 브라우저에서 한다(빌드 시 주입한 개인키, `qztray.ts` 주석 참고).
 * 서버 서명 API 로 옮기는 일은 두 화면을 함께 바꾸는 후속 작업이다 — 그때 이 파일의 `configureSecurity` 만 바꾸면 된다.
 */

let securityConfigured = false

/** 인증서·서명 설정(1.0 `connectWithPrinter` 의 `qz.security.*` 부분). 한 번만 등록한다. */
const configureSecurity = (): void => {
  if (securityConfigured) return
  if (!QZ_TRAY_CERT || !QZ_TRAY_PRIVATE_KEY) {
    throw new Error('QZ Tray 인증정보가 설정되지 않았습니다.')
  }

  qz.security.setCertificatePromise((resolve) => resolve(QZ_TRAY_CERT))
  qz.security.setSignatureAlgorithm('SHA512')
  qz.security.setSignaturePromise((toSign: string) => (resolve, reject) => {
    try {
      const pk = KEYUTIL.getKey(QZ_TRAY_PRIVATE_KEY)
      const sig = new KJUR.crypto.Signature({ alg: 'SHA512withRSA' })
      sig.init(pk)
      sig.updateString(toSign)
      resolve(stob64(hextorstr(sig.sign())))
    } catch (err) {
      reject(String(err))
    }
  })

  securityConfigured = true
}

const ensureConnected = async (): Promise<void> => {
  configureSecurity()
  if (qz.websocket.isActive()) return
  await qz.websocket.connect({ port: { secure: [8181], insecure: [8182] }, usingSecure: true, retries: 0, keepAlive: 60, delay: 0 })
}

/** 프린터·용지 규격. 화면별 고정값은 각 화면 폴더에 둔다. */
export interface PrinterConfig {
  printerName: string
  width: number
  height: number
  units: 'in' | 'cm'
  /** 1.0 GL 라벨은 `rasterize: false` 를 명시했다. 없으면 qz 기본값. */
  rasterize?: boolean
  /** 1.0 GL 일본 규격 라벨은 `reverse-landscape`. 없으면 qz 기본값. */
  orientation?: 'portrait' | 'landscape' | 'reverse-landscape'
}

export interface PrintJob extends PrinterConfig {
  /** 인쇄할 영역의 `innerHTML`(1.0 과 같은 방식 — `type:'pixel', format:'html'`). */
  html: string
}

/**
 * qz-tray 로 HTML 영역을 인쇄한다.
 *
 * qz-tray 데스크톱 에이전트가 로컬에 없으면(미설치·미실행) `websocket.connect` 가 실패한다.
 * 이 함수는 실패를 삼키지 않고 그대로 던진다 — 삼킬지(저장 흐름을 막지 않기) 알릴지(사용자가 직접 누른 인쇄)는 호출부가 정한다.
 */
export const printHtml = async ({ printerName, width, height, units, rasterize, orientation, html }: PrintJob): Promise<void> => {
  await ensureConnected()
  const config = qz.configs.create(printerName, {
    ...(rasterize === undefined ? {} : { rasterize }),
    size: { width, height },
    units,
    scaleContent: false,
    ...(orientation === undefined ? {} : { orientation }),
  })
  await qz.print(config, [{ type: 'pixel', format: 'html', flavor: 'plain', data: html }])
}
